const domainAliases = {
  EDUCATION: 'EDUCATION',
  ACADEMY: 'EDUCATION',
  RETAIL: 'RETAIL',
  FLEX_OFFICE: 'FLEX_OFFICE',
  FLEXOFFICE: 'FLEX_OFFICE',
  FLEX: 'FLEX_OFFICE',
  WAAS: 'WAAS'
};

const sharedThemeDefaults = {
  successColor: '#16a34a',
  warningColor: '#f59e0b',
  errorColor: '#dc2626',
  letterSpacing: '0',
  sectionHeadingScale: '1',
  sectionSpacing: '80px',
  gridGap: '24px',
  containerPadding: '24px',
  mobilePadding: '16px',
  buttonHeight: '46px',
  imageRadius: '16px',
  spacingScale: 'comfortable'
};

const domainThemeDefaults = {
  EDUCATION: {
    ...sharedThemeDefaults,
    primary: '#3B9394',
    secondary: '#746EAC',
    accent: '#2563eb',
    background: '#ffffff',
    surface: '#ffffff',
    color: '#1f2937',
    mutedColor: '#4b5563',
    borderColor: '#e5e7eb',
    fontFamily: 'Inter, Arial, sans-serif',
    headingFontFamily: 'Inter, Arial, sans-serif',
    baseFontSize: '16px',
    headingWeight: '800',
    bodyWeight: '400',
    lineHeight: '1.6',
    pageWidth: '1280px',
    buttonRadius: '8px',
    cardRadius: '16px',
    cardShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
    inputRadius: '8px',
    footerBackground: '#f3f4f6'
  },
  RETAIL: {
    ...sharedThemeDefaults,
    primary: '#0891b2',
    secondary: '#232f3e',
    accent: '#B88E2F',
    background: '#f8fafc',
    surface: '#ffffff',
    color: '#111827',
    mutedColor: '#6b7280',
    borderColor: '#e5e7eb',
    fontFamily: 'Inter, Arial, sans-serif',
    headingFontFamily: 'Inter, Arial, sans-serif',
    baseFontSize: '16px',
    headingWeight: '800',
    bodyWeight: '400',
    lineHeight: '1.6',
    pageWidth: '1280px',
    buttonRadius: '999px',
    cardRadius: '14px',
    cardShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
    inputRadius: '10px',
    footerBackground: '#232f3e'
  },
  FLEX_OFFICE: {
    ...sharedThemeDefaults,
    primary: '#2563eb',
    secondary: '#0f172a',
    accent: '#1d4ed8',
    background: '#f8fafc',
    surface: '#ffffff',
    color: '#1e293b',
    mutedColor: '#64748b',
    borderColor: '#e2e8f0',
    fontFamily: 'Inter, Arial, sans-serif',
    headingFontFamily: 'Inter, Arial, sans-serif',
    baseFontSize: '16px',
    headingWeight: '800',
    bodyWeight: '400',
    lineHeight: '1.6',
    pageWidth: '1280px',
    buttonRadius: '12px',
    cardRadius: '20px',
    cardShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
    inputRadius: '10px',
    footerBackground: '#0f172a'
  },
  WAAS: {
    ...sharedThemeDefaults,
    primary: '#07111f',
    secondary: '#d8c2b6',
    accent: '#0b0f16',
    background: '#e6e8eb',
    surface: '#ffffff',
    color: '#0b0f16',
    mutedColor: '#5f6670',
    borderColor: '#d5d8dd',
    fontFamily: 'Inter, Arial, sans-serif',
    headingFontFamily: 'Inter, Arial, sans-serif',
    baseFontSize: '14px',
    headingWeight: '300',
    bodyWeight: '400',
    lineHeight: '1.55',
    pageWidth: '1220px',
    buttonRadius: '999px',
    cardRadius: '24px',
    cardShadow: '0 24px 56px rgba(24, 34, 46, 0.12)',
    inputRadius: '14px',
    imageRadius: '24px',
    footerBackground: '#e6e8eb'
  }
};

export const normalizeTemplateDomain = (domainType = '') => {
  const value = String(domainType || '').trim().replace(/[\s-]+/g, '_').toUpperCase();
  return domainAliases[value] || value;
};

const pickFirst = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const collectThemeTokens = (template = {}) => {
  const attributes = template.attributes || {};
  const rawTheme = attributes.theme || template.theme || {};
  const footer = attributes.footer || rawTheme.footer || template.footer || {};
  const typography = rawTheme.typography || attributes.typography || template.typography || {};
  const brand = attributes.brand || rawTheme.brand || template.brand || {};
  const colors = rawTheme.colors || attributes.colors || brand.colors || {};
  const layout = rawTheme.layout || attributes.layout || template.layout || {};
  const components = rawTheme.components || attributes.components || template.components || {};
  const buttons = components.buttons || rawTheme.buttons || attributes.buttons || {};
  const cards = components.cards || rawTheme.cards || attributes.cards || {};
  const inputs = components.inputs || rawTheme.inputs || attributes.inputs || {};
  const images = components.images || rawTheme.images || attributes.images || {};
  const spacing = rawTheme.spacing || attributes.spacing || {};
  const states = rawTheme.states || rawTheme.status || colors.states || colors.status || {};

  return {
    ...rawTheme,
    primary: pickFirst(rawTheme.primary, rawTheme.primaryColor, rawTheme.brandPrimary, colors.primary, colors.primaryColor, brand.primary, brand.primaryColor, attributes.primary, attributes.primaryColor, template.primaryColor),
    secondary: pickFirst(rawTheme.secondary, rawTheme.secondaryColor, rawTheme.brandSecondary, colors.secondary, colors.secondaryColor, brand.secondary, brand.secondaryColor, attributes.secondary, attributes.secondaryColor, template.secondaryColor),
    accent: pickFirst(rawTheme.accent, rawTheme.accentColor, colors.accent, colors.accentColor, attributes.accent, attributes.accentColor),
    background: pickFirst(rawTheme.background, rawTheme.backgroundColor, colors.background, colors.backgroundColor, attributes.background, attributes.backgroundColor),
    surface: pickFirst(rawTheme.surface, rawTheme.surfaceColor, colors.surface, colors.surfaceColor, attributes.surface, attributes.surfaceColor),
    color: pickFirst(rawTheme.color, rawTheme.textColor, colors.text, colors.color, colors.textColor, attributes.color, attributes.textColor),
    mutedColor: pickFirst(rawTheme.mutedColor, rawTheme.secondaryTextColor, colors.muted, colors.mutedColor, colors.secondaryTextColor, attributes.mutedColor, attributes.secondaryTextColor),
    borderColor: pickFirst(rawTheme.borderColor, colors.border, colors.borderColor, attributes.borderColor),
    successColor: pickFirst(rawTheme.successColor, colors.success, colors.successColor, states.success, states.successColor, attributes.successColor),
    warningColor: pickFirst(rawTheme.warningColor, colors.warning, colors.warningColor, states.warning, states.warningColor, attributes.warningColor),
    errorColor: pickFirst(rawTheme.errorColor, colors.error, colors.errorColor, states.error, states.errorColor, attributes.errorColor),
    fontFamily: pickFirst(rawTheme.fontFamily, rawTheme.bodyFontFamily, typography.fontFamily, typography.bodyFontFamily, attributes.fontFamily, template.fontFamily),
    headingFontFamily: pickFirst(rawTheme.headingFontFamily, rawTheme.headingFont, typography.headingFontFamily, typography.headingFont, attributes.headingFontFamily),
    baseFontSize: pickFirst(rawTheme.baseFontSize, rawTheme.fontSize, typography.baseFontSize, typography.fontSize, attributes.baseFontSize),
    headingWeight: pickFirst(rawTheme.headingWeight, typography.headingWeight, attributes.headingWeight),
    bodyWeight: pickFirst(rawTheme.bodyWeight, typography.bodyWeight, attributes.bodyWeight),
    lineHeight: pickFirst(rawTheme.lineHeight, typography.lineHeight, attributes.lineHeight),
    letterSpacing: pickFirst(rawTheme.letterSpacing, typography.letterSpacing, attributes.letterSpacing),
    sectionHeadingScale: pickFirst(rawTheme.sectionHeadingScale, typography.sectionHeadingScale, attributes.sectionHeadingScale),
    pageWidth: pickFirst(rawTheme.pageWidth, rawTheme.containerWidth, layout.pageWidth, layout.containerWidth, attributes.pageWidth, attributes.containerWidth),
    sectionSpacing: pickFirst(rawTheme.sectionSpacing, spacing.sectionSpacing, layout.sectionSpacing, attributes.sectionSpacing),
    gridGap: pickFirst(rawTheme.gridGap, spacing.gridGap, layout.gridGap, attributes.gridGap),
    containerPadding: pickFirst(rawTheme.containerPadding, spacing.containerPadding, layout.containerPadding, attributes.containerPadding),
    mobilePadding: pickFirst(rawTheme.mobilePadding, spacing.mobilePadding, layout.mobilePadding, attributes.mobilePadding),
    spacingScale: pickFirst(rawTheme.spacingScale, spacing.scale, layout.spacingScale, attributes.spacingScale),
    buttonRadius: pickFirst(rawTheme.buttonRadius, buttons.radius, buttons.borderRadius, attributes.buttonRadius),
    buttonHeight: pickFirst(rawTheme.buttonHeight, buttons.height, attributes.buttonHeight),
    cardRadius: pickFirst(rawTheme.cardRadius, cards.radius, cards.borderRadius, attributes.cardRadius),
    cardShadow: pickFirst(rawTheme.cardShadow, cards.shadow, attributes.cardShadow),
    inputRadius: pickFirst(rawTheme.inputRadius, inputs.radius, inputs.borderRadius, attributes.inputRadius),
    imageRadius: pickFirst(rawTheme.imageRadius, images.radius, images.borderRadius, attributes.imageRadius),
    footerBackground: pickFirst(footer.backgroundColor, rawTheme.footerBackground, rawTheme.footerBackgroundColor, attributes.footerBackground),
    footerTextColor: pickFirst(footer.textColor, rawTheme.footerTextColor, rawTheme.footerColor, attributes.footerTextColor),
    footerHeadingColor: pickFirst(footer.headingColor, rawTheme.footerHeadingColor, attributes.footerHeadingColor),
    footerLinkColor: pickFirst(footer.linkColor, rawTheme.footerLinkColor, attributes.footerLinkColor),
    footerBorderColor: pickFirst(footer.borderColor, rawTheme.footerBorderColor, attributes.footerBorderColor)
  };
};

export const normalizeTemplateTheme = (template, domainType) => {
  const resolvedDomain = normalizeTemplateDomain(domainType || template?.domain?.type || template?.attributes?.sourceDomain || 'EDUCATION');
  const defaults = domainThemeDefaults[resolvedDomain] || domainThemeDefaults.EDUCATION;
  const rawTheme = collectThemeTokens(template);
  const primary = rawTheme.primary || defaults.primary;
  const secondary = rawTheme.secondary || defaults.secondary;
  const fontFamily = rawTheme.fontFamily || rawTheme.bodyFontFamily || defaults.fontFamily;
  const headingFontFamily = rawTheme.headingFontFamily || rawTheme.headingFont || fontFamily || defaults.headingFontFamily;
  const color = rawTheme.color || rawTheme.textColor || defaults.color;
  const accent = rawTheme.accent || rawTheme.accentColor || defaults.accent || primary;
  const borderColor = rawTheme.borderColor || defaults.borderColor;
  const background = rawTheme.background || rawTheme.backgroundColor || defaults.background;

  return {
    ...defaults,
    ...rawTheme,
    primary,
    primaryColor: primary,
    secondary,
    secondaryColor: secondary,
    accent,
    background,
    surface: rawTheme.surface || rawTheme.surfaceColor || defaults.surface,
    color,
    mutedColor: rawTheme.mutedColor || rawTheme.secondaryTextColor || defaults.mutedColor,
    borderColor,
    successColor: rawTheme.successColor || defaults.successColor,
    warningColor: rawTheme.warningColor || defaults.warningColor,
    errorColor: rawTheme.errorColor || defaults.errorColor,
    fontFamily,
    headingFontFamily,
    baseFontSize: rawTheme.baseFontSize || rawTheme.fontSize || defaults.baseFontSize,
    headingWeight: rawTheme.headingWeight || defaults.headingWeight,
    bodyWeight: rawTheme.bodyWeight || defaults.bodyWeight,
    lineHeight: rawTheme.lineHeight || defaults.lineHeight,
    letterSpacing: rawTheme.letterSpacing || defaults.letterSpacing,
    sectionHeadingScale: rawTheme.sectionHeadingScale || defaults.sectionHeadingScale,
    pageWidth: rawTheme.pageWidth || rawTheme.containerWidth || defaults.pageWidth,
    sectionSpacing: rawTheme.sectionSpacing || defaults.sectionSpacing,
    gridGap: rawTheme.gridGap || defaults.gridGap,
    containerPadding: rawTheme.containerPadding || defaults.containerPadding,
    mobilePadding: rawTheme.mobilePadding || defaults.mobilePadding,
    spacingScale: rawTheme.spacingScale || defaults.spacingScale,
    buttonRadius: rawTheme.buttonRadius || defaults.buttonRadius,
    buttonHeight: rawTheme.buttonHeight || defaults.buttonHeight,
    cardRadius: rawTheme.cardRadius || defaults.cardRadius,
    cardShadow: rawTheme.cardShadow || defaults.cardShadow,
    inputRadius: rawTheme.inputRadius || defaults.inputRadius,
    imageRadius: rawTheme.imageRadius || defaults.imageRadius,
    footerBackground: rawTheme.footerBackground || background || defaults.footerBackground,
    footerTextColor: rawTheme.footerTextColor || color,
    footerHeadingColor: rawTheme.footerHeadingColor || accent || color,
    footerLinkColor: rawTheme.footerLinkColor || accent || primary,
    footerBorderColor: rawTheme.footerBorderColor || borderColor
  };
};

export const getDomainThemeDefaults = (domainType) => ({
  ...(domainThemeDefaults[normalizeTemplateDomain(domainType)] || domainThemeDefaults.EDUCATION)
});
